import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Field, Input, Select, Avatar } from "./ui";
import { toast } from "sonner";
import { Plus, Trash2, CalendarDays, LayoutList } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const STATUSES = ["scheduled", "active", "completed", "absent", "swapped"];

export default function ShiftScheduling() {
  const { locationId } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [start, setStart] = useState(iso(new Date()));
  const [view, setView] = useState("week");
  const [rows, setRows] = useState([]);
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("");

  const days = view === "week" ? 7 : view === "day" ? 1 : 30;
  const end = iso(addDays(new Date(start), days - 1));

  const load = () => api.get("/hr/shifts", {
    params: { date_from: start, date_to: end, location_id: locationId || undefined },
  }).then(setRows).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    api.get("/hr/shift-types").then(setTypes);
    api.get("/hr/employees", { params: { location_id: locationId || undefined } }).then(setEmployees);
    /* eslint-disable-next-line */
  }, [locationId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, view, locationId]);
  useEffect(() => {
    if (params.get("new")) { setForm({ employee_id: "", shift_type_id: "", date: start, status: "scheduled" }); setParams({}); }
    /* eslint-disable-next-line */
  }, [params]);

  const dayList = useMemo(() => Array.from({ length: days }, (_, i) => iso(addDays(new Date(start), i))), [start, days]);
  const visible = useMemo(() => rows.filter((r) =>
    (!typeFilter || r.shift_type_id === typeFilter) &&
    (!staffFilter || (r.employee_name || "").toLowerCase().includes(staffFilter.toLowerCase()))
  ), [rows, typeFilter, staffFilter]);
  const byDay = useMemo(() => {
    const m = {};
    visible.forEach((r) => { (m[r.date] = m[r.date] || []).push(r); });
    return m;
  }, [visible]);

  const save = async () => {
    try {
      const res = await api.post("/hr/shifts", form);
      if (res.conflicts?.length) toast.error(`Conflict: ${res.conflicts[0].reason}`);
      if (res.created?.length) { toast.success("Shift created"); setForm(null); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (id) => {
    try { await api.del(`/hr/shifts/${id}`); toast.success("Shift removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const setStatus = async (r, status) => {
    try { await api.put(`/hr/shifts/${r.id}`, { status }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="hr-shifts-page">
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <Field label="From" className="lg:w-52"><Input data-testid="shift-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="View" className="lg:w-44">
            <Select data-testid="shift-view" value={view} onChange={(e) => setView(e.target.value)}>
              <option value="day">Daily</option><option value="week">Weekly</option><option value="month">Monthly</option>
            </Select>
          </Field>
          <Field label="Shift type" className="lg:w-48">
            <Select data-testid="shift-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All shift types</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </Select>
          </Field>
          <Field label="Staff" className="lg:w-52">
            <Input data-testid="shift-staff-filter" value={staffFilter} placeholder="Filter by staff name"
              onChange={(e) => setStaffFilter(e.target.value)} />
          </Field>
          <div className="flex-1 flex flex-wrap items-center gap-2">
            {types.map((t) => (
              <span key={t.id} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full"
                style={{ background: HC.panelSoft, color: HC.text, border: `1px solid ${HC.border}` }}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />{t.name} {t.start_time}–{t.end_time}
              </span>
            ))}
          </div>
          <Btn onClick={() => setForm({ employee_id: "", shift_type_id: types[0]?.id || "", date: start, status: "scheduled" })}
            data-testid="hr-create-shift"><Plus size={15} /> Create Shift</Btn>
        </div>
      </Card>

      <Card testid="hr-roster">
        <SectionHead title="Roster" sub={`${start} → ${end} · ${visible.length} of ${rows.length} assignment(s)`}
          right={<span className="inline-flex items-center gap-1.5 text-[11.5px]" style={{ color: HC.soft }}><CalendarDays size={14} /> Conflicts blocked automatically</span>} />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {dayList.map((d) => (
            <div key={d} className="rounded-xl border p-3" style={{ borderColor: HC.border, background: HC.panelSoft }} data-testid={`roster-day-${d}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[12px] font-bold" style={{ color: HC.text }}>
                  {new Date(d).toLocaleDateString("en-NG", { weekday: "short", day: "numeric", month: "short" })}
                </span>
                <span className="text-[11px]" style={{ color: HC.soft }}>{(byDay[d] || []).length}</span>
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {(byDay[d] || []).slice(0, 40).map((r) => (
                  <div key={r.id} className="flex items-center gap-2 bg-white rounded-lg px-2 py-1.5 border" style={{ borderColor: HC.borderSoft }}>
                    <span className="w-1.5 h-6 rounded-full shrink-0" style={{ background: r.shift_color || HC.gold }} />
                    <span className="min-w-0">
                      <span className="block text-[11.5px] font-semibold truncate" style={{ color: HC.text }}>{r.employee_name}</span>
                      <span className="block text-[10px]" style={{ color: HC.soft }}>{r.shift_name} · {r.start_time}–{r.end_time}</span>
                    </span>
                    <span className="flex-1" />
                    <button onClick={() => remove(r.id)} data-testid={`shift-del-${r.id}`} style={{ color: HC.soft }}><Trash2 size={13} /></button>
                  </div>
                ))}
                {!(byDay[d] || []).length && <div className="text-[11.5px] py-3 text-center" style={{ color: HC.soft }}>No shifts</div>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <SectionHead title="Assignments" sub="Update shift status as the day progresses" right={<LayoutList size={16} style={{ color: HC.soft }} />} />
        <Table
          testid="hr-shift-assignments" rows={visible.slice(0, 60)} empty="No shift assignments"
          columns={[
            { key: "employee_name", label: "EMPLOYEE", render: (r) => (
                <span className="flex items-center gap-2"><Avatar name={r.employee_name} /><span className="font-medium">{r.employee_name}</span></span>) },
            { key: "position", label: "POSITION" },
            { key: "location_name", label: "LOCATION" },
            { key: "date", label: "DATE" },
            { key: "shift_name", label: "SHIFT", render: (r) => <Pill status="scheduled" text={r.shift_name} /> },
            { key: "time", label: "TIME", render: (r) => <span className="tabular-nums">{r.start_time} – {r.end_time}</span> },
            { key: "status", label: "STATUS", render: (r) => (
                <select value={r.status} onChange={(e) => setStatus(r, e.target.value)} data-testid={`shift-status-${r.id}`}
                  className="h-8 px-2 rounded-lg text-[11.5px] font-semibold bg-white outline-none" style={{ border: `1px solid ${HC.border}`, color: HC.text }}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>) },
          ]}
        />
      </Card>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create shift</DialogTitle>
            <DialogDescription>Double-booking an employee on the same date is blocked.</DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-3">
              <Field label="Employee">
                <Select data-testid="shift-employee" value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
                  <option value="">Select employee</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.department_name}</option>)}
                </Select>
              </Field>
              <Field label="Shift type">
                <Select data-testid="shift-type" value={form.shift_type_id} onChange={(e) => setForm({ ...form, shift_type_id: e.target.value })}>
                  <option value="">Select shift</option>
                  {types.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.start_time}–{t.end_time})</option>)}
                </Select>
              </Field>
              <Field label="Date"><Input data-testid="shift-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
              <Field label="Notes"><Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setForm(null)}>Cancel</Btn>
            <Btn onClick={save} data-testid="shift-save">Create shift</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
